import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getExecutiveSummary } from '../../services/executiveAnalytics';
import { describeHttpError } from '../../utils/apiError';
import { toScopeQuery } from '../../utils/scopeSelection';

const isCanceled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

/** ТЗ №1 §4.2 / §12.2: 404 — объект недоступен, 422 — ошибка фильтра, 5xx/network — error + Retry. */
const classifyError = (error) => {
  const status = error?.response?.status;
  if (status === 404) return { kind: 'not_found', message: 'Объект недоступен' };
  if (status === 422) return { kind: 'validation', message: describeHttpError(error, 'Некорректный период или фильтр') };
  if (status >= 500) return { kind: 'server', message: describeHttpError(error, 'Ошибка сервера') };
  if (status) return { kind: 'server', message: describeHttpError(error, 'Не удалось загрузить сводку') };
  return { kind: 'network', message: 'Нет связи с сервером' };
};

/**
 * ТЗ №1 §12.1: только последний запрос обновляет state; при reload старые
 * данные не обнуляются (показываются stale/dimmed); ошибка не превращает
 * цифры в нули. Предыдущий запрос отменяется через AbortController.
 *
 * SCOPE-MULTI01 (ТЗ v4 §21): scope уходит массивами; tableParams — per-table
 * фильтры/сортировка/пагинация того же роута. Все блоки страницы обновляются
 * одним подтверждённым снимком ответа, поэтому и scope, и table-параметры
 * живут в одном запросе, а не в нескольких параллельных.
 *
 * BM-FE-TZ-002B: тот же хук обслуживает вкладку «Бизнес-метрики» (fetcher =
 * getBusinessSummary) — роут есть в production, ошибки те же.
 */
export const useExecutiveSummary = ({
  from, to, scope, tableParams, enabled = true, fetcher = getExecutiveSummary,
}) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const seqRef = useRef(0);

  const requestParams = useMemo(
    () => ({ from, to, ...toScopeQuery(scope), ...(tableParams || {}) }),
    [from, to, scope, tableParams]
  );
  // Ключ по значению: новая ссылка на тот же scope не должна перезапрашивать.
  const requestKey = JSON.stringify(requestParams);

  useEffect(() => {
    if (!enabled || !from || !to) return undefined;
    const seq = ++seqRef.current;
    const controller = new AbortController();
    setStatus('loading');
    setError(null);
    fetcher(JSON.parse(requestKey), { signal: controller.signal })
      .then((response) => {
        if (seq !== seqRef.current) return;
        setData(response);
        setStatus('ready');
      })
      .catch((requestError) => {
        if (seq !== seqRef.current || isCanceled(requestError)) return;
        setError(classifyError(requestError));
        setStatus('error');
      });
    return () => controller.abort();
  }, [requestKey, from, to, enabled, reloadToken, fetcher]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  return { data, status, error, reload };
};

export default useExecutiveSummary;
